import { Fragment, ReactNode, useState } from "react";
import { CloseOutlined, FilterOutlined, LeftOutlined, SettingOutlined } from "@ant-design/icons";
import { clsx } from "clsx";
import { Button } from "../Button";
import { Checkbox } from "../Checkbox";
import { Popover } from "../Popover";
import { Switch } from "../Switch";
import { ToolbarButton } from "../ToolbarButton";
import classes from "./DataTable.module.less";

/**
 * How a column filters its rows.
 *
 * `enum`: a fixed set of values, several of which can be checked at once; a
 * row stays when its value is among them.
 *
 * `pick`: one value chosen in an editor the caller renders (a category tree,
 * a user list), so the table stays free of what the values are. Picking a
 * value applies it at once.
 */
export type DataTableFilterSpec<T> =
    | {
          type: "enum";
          options: { value: string; label: ReactNode }[];
          /** The row's value, matched against the checked ones. */
          value: (row: T) => string | undefined;
      }
    | {
          type: "pick";
          render: (
              value: string | undefined,
              onChange: (value: string | undefined) => void
          ) => ReactNode;
          /** How the chip reads the picked value. */
          label: (value: string) => ReactNode;
          match: (row: T, value: string) => boolean;
      };

/** The active filters: the checked values (or the one picked value) per column key. */
export type DataTableActiveFilters = Record<string, string[]>;

export interface FilterableColumn<T> {
    key: string;
    title: string;
    filter: DataTableFilterSpec<T>;
}

const popoverProps = {
    trigger: "click" as const,
    showArrow: false,
    // antd keeps the arrow's gap even without the arrow; sit 4px under the
    // button like a select's dropdown does.
    overlayStyle: { paddingTop: 4 },
    flush: true,
    destroyTooltipOnHide: true,
};

/** Whether a row passes every active filter the table knows a column for. */
export function passesFilters<T>(
    row: T,
    columns: FilterableColumn<T>[],
    active: DataTableActiveFilters
): boolean {
    return Object.entries(active).every(([key, values]) => {
        const spec = columns.find((c) => c.key === key)?.filter;
        if (!spec || values.length === 0) return true;
        if (spec.type === "enum") {
            const value = spec.value(row);
            return value != null && values.includes(value);
        }
        return spec.match(row, values[0]);
    });
}

interface ColumnFiltersProps<T> {
    columns: FilterableColumn<T>[];
    active: DataTableActiveFilters;
    /** Undefined clears the column's filter. */
    onChange: (key: string, values: string[] | undefined) => void;
}

/**
 * The Filter button, counting the filters in force. It opens a popover
 * listing the filterable columns, each showing its filter as a chip with an ×
 * to lift it; picking a column shows its editor.
 */
export function ColumnFilters<T>({ columns, active, onChange }: ColumnFiltersProps<T>) {
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState<string | undefined>();
    // The enum editor's checks, applied as a whole.
    const [draft, setDraft] = useState<string[]>([]);

    const column = columns.find((c) => c.key === editing);

    const openEditor = (key: string) => {
        setEditing(key);
        setDraft(active[key] ?? []);
        setOpen(true);
    };
    const toggleOpen = (next: boolean) => {
        setOpen(next);
        if (next) setEditing(undefined);
    };
    const apply = (values: string[] | undefined) => {
        if (editing) onChange(editing, values?.length ? values : undefined);
        setOpen(false);
    };

    // What a chip says after the column's title.
    const summary = (col: FilterableColumn<T>, values: string[]): ReactNode => {
        if (col.filter.type === "pick") return col.filter.label(values[0]);
        if (values.length > 3) return `${values.length} selected`;
        const options = col.filter.options;
        return values.map((value, i) => (
            <Fragment key={value}>
                {i > 0 && ", "}
                {options.find((o) => o.value === value)?.label ?? value}
            </Fragment>
        ));
    };

    const activeCount = columns.filter((c) => active[c.key]?.length).length;

    const content = !column ? (
        <div className={classes.menu}>
            <div className={classes["menu-title"]}>Filter</div>
            <div className={classes["menu-options"]} role="menu">
                {columns.map((c) => {
                    const values = active[c.key];
                    return (
                        <div key={c.key} className={classes["menu-row"]}>
                            <button
                                type="button"
                                role="menuitem"
                                className={classes["menu-row__title"]}
                                onClick={() => openEditor(c.key)}
                            >
                                {c.title}
                            </button>
                            {values?.length ? (
                                <span className={clsx(classes.chip, classes["chip--small"])}>
                                    <button
                                        type="button"
                                        className={classes.chip__label}
                                        onClick={() => openEditor(c.key)}
                                    >
                                        {summary(c, values)}
                                    </button>
                                    <button
                                        type="button"
                                        className={classes.chip__remove}
                                        aria-label={`Remove the ${c.title} filter`}
                                        onClick={() => onChange(c.key, undefined)}
                                    >
                                        <CloseOutlined />
                                    </button>
                                </span>
                            ) : null}
                        </div>
                    );
                })}
            </div>
        </div>
    ) : (
        <div className={classes.menu}>
            <div className={classes["menu-header"]}>
                <button
                    type="button"
                    className={classes["menu-back"]}
                    aria-label="Back to the columns"
                    onClick={() => setEditing(undefined)}
                >
                    <LeftOutlined />
                </button>
                {column.title}
            </div>
            {column.filter.type === "enum" ? (
                <>
                    <div className={classes["menu-options"]}>
                        {column.filter.options.map((option) => (
                            <label key={option.value} className={classes["menu-row"]}>
                                <Checkbox
                                    checked={draft.includes(option.value)}
                                    onChange={(e) =>
                                        setDraft((current) =>
                                            e.target.checked
                                                ? [...current, option.value]
                                                : current.filter((v) => v !== option.value)
                                        )
                                    }
                                />
                                {option.label}
                            </label>
                        ))}
                    </div>
                    <div className={classes["menu-footer"]}>
                        <Button size="small" onClick={() => apply(undefined)}>
                            Clear
                        </Button>
                        <Button size="small" type="primary" onClick={() => apply(draft)}>
                            Apply
                        </Button>
                    </div>
                </>
            ) : (
                column.filter.render(active[column.key]?.[0], (value) =>
                    apply(value ? [value] : undefined)
                )
            )}
        </div>
    );

    return (
        <Popover
            {...popoverProps}
            placement="bottomRight"
            open={open}
            onOpenChange={toggleOpen}
            content={content}
        >
            <ToolbarButton icon={<FilterOutlined />} active={activeCount > 0} count={activeCount}>
                Filter
            </ToolbarButton>
        </Popover>
    );
}

interface ColumnsMenuProps {
    columns: { key: string; title: string }[];
    hidden: string[];
    onToggle: (key: string) => void;
}

/** The settings button at the toolbar's end: a switch per column to show or hide it. */
export function ColumnsMenu({ columns, hidden, onToggle }: ColumnsMenuProps) {
    const [open, setOpen] = useState(false);
    return (
        <Popover
            {...popoverProps}
            placement="bottomRight"
            open={open}
            onOpenChange={setOpen}
            content={
                <div className={classes.menu}>
                    <div className={classes["menu-title"]}>Columns</div>
                    <div className={classes["menu-options"]}>
                        {columns.map((column) => (
                            <label
                                key={column.key}
                                className={clsx(classes["menu-row"], classes["menu-row--toggle"])}
                            >
                                {column.title}
                                <Switch
                                    size="small"
                                    checked={!hidden.includes(column.key)}
                                    onChange={() => onToggle(column.key)}
                                />
                            </label>
                        ))}
                    </div>
                </div>
            }
        >
            <ToolbarButton icon={<SettingOutlined />} aria-label="Columns" title="Columns" />
        </Popover>
    );
}
